import { useEffect, useState } from 'react';
import { TcorFollowingSteps } from '@frontend/components/pages/customControls/TCOR/v1/TcorFollowingSteps';
import type { IRequestGetOne } from '../../../../interfaces/requestGetOne';
import type { ICustomControlRequestReadProps } from '../../interfaces/customControlRequestReadProps';
import type { ICustomControlData } from './TcorV1RequestAddEdit';
import { getServiceLabel } from '../../../../../../common/customControls/getServiceLabel';
import { ProcessEntity } from '../../../../../../common/enums/processEntity';
import { RequestStatus } from '../../../../../../common/enums/requestStatus';
import { calculateBmi } from '../../../../../../common/utils/bmi';
import { getDisplayName } from '../../../../../../common/utils/getDisplayName';
import { translateKey } from '../../../../../translate';
import { RequestReadButtons } from '../../../../elements/carePathButtons/RequestReadButtons';
import { FormDivider, FormParagraph, HeaderDivider } from '../../../../elements/common';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { HorizontalFormGroupRead } from '../../../../elements/forms/horizontalFormGroupRead/HorizontalFormGroupRead';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { ProcessContainer } from '../../../episodes/common';
import { ProcessItemFooter } from '../../../episodes/ProcessItemFooter';
import { ProcessItemHeader } from '../../../episodes/ProcessItemHeader';
import { ProfessionalSummaryDataRead } from '../../../professionalSummaryData/ProfessionalSummaryDataRead';
import { HideRequestedExaminationInTaskListAndHomeCheckbox } from '../../../requests/HideRequestedExaminationInTaskListAndHomeCheckbox';
import { LichamelijkOnderzoekRead } from '../../TCCr/v1/LichamelijkOnderzoek';
import { RisicofactorenRead } from '../../TCCr/v1/Risicofactoren';
import { VoorgeschiedenisRead } from '../../TCCr/v1/Voorgeschiedenis';
import { CustomControlCollapseBody } from '../../utils/v1/CustomControlCollapseBody';
import { MedicationsRead } from '../../utils/v1/medications/MedicationsRead';
import { ProfileImages } from '../../utils/v1/ProfileImages';
import { TxcsMedicationsList } from '../../utils/v1/TxcsMedicationsList';
import { TcorV1RequestSummary } from './TcorV1RequestSummary';

interface IRequestReadProps extends IRequestGetOne {
    customControlData: ICustomControlData;
}

interface ITcorV1RequestReadProps extends ICustomControlRequestReadProps {
    request: IRequestReadProps;
}

export function TcorV1RequestRead(props: ITcorV1RequestReadProps) {
    const [isFooterVisible, setIsFooterVisible] = useState(false);

    useEffect(() => {
        checkIsFooterVisible();
    }, [props.request.customControlData, props.request.status]);

    return (
        <ProcessContainer>
            <ProcessItemHeader
                date={props.process.dateSent ?? props.request?.dateSent}
                isExaminationRequest
                isFirstItem={props.isFirstItem}
                isFirstProcess={props.itemState.isFirstProcess}
                isItemOpen={props.itemState.isOpen}
                onToggle={async (isOpened) => await props.updateItemState({ ...props.itemState, isOpen: isOpened })}
                organizationName={props.process.requesterOrganization?.name}
                practitionerName={
                    props.process.requesterPractitioner
                        ? getDisplayName(props.process.requesterPractitioner)
                        : undefined
                }
                process={props.process}
                title={getServiceLabel(props.process.code, 'request', props.process.name)}
            />

            <TcorV1RequestSummary {...props} />

            <CustomControlCollapseBody isOpen={props.itemState.isOpen}>
                {showAnamnese() && <HeaderDivider text="Anamnese" />}
                {props.request.customControlData.bloeddruk_systolisch && (
                    <HorizontalFormGroupRead>
                        <FormLabel>Bloeddruk systolisch</FormLabel>
                        <InputContainer>
                            <FormParagraph>
                                {props.request.customControlData.bloeddruk_systolisch + ' mmHg'}
                            </FormParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}
                {props.request.customControlData.bloeddruk_diastolisch && (
                    <HorizontalFormGroupRead>
                        <FormLabel>Bloeddruk diastolisch</FormLabel>
                        <InputContainer>
                            <FormParagraph>
                                {props.request.customControlData.bloeddruk_diastolisch + ' mmHg'}
                            </FormParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}

                {props.request.customControlData.lengte !== null && props.request.customControlData.gewicht !== null ? (
                    <HorizontalFormGroupRead>
                        <FormLabel>BMI</FormLabel>
                        <InputContainer>
                            <FormParagraph>
                                {calculateBmi(
                                    props.request.customControlData.gewicht,
                                    props.request.customControlData.lengte
                                )}
                            </FormParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                ) : null}

                {props.request.customControlData.voorgeschiedenis && (
                    <VoorgeschiedenisRead {...props.request.customControlData.voorgeschiedenis} />
                )}
                {props.request.customControlData.risicofactoren && (
                    <RisicofactorenRead {...props.request.customControlData.risicofactoren} />
                )}
                {props.request.customControlData.lichamelijkOnderzoek && (
                    <LichamelijkOnderzoekRead {...props.request.customControlData.lichamelijkOnderzoek} />
                )}
                {props.request.customControlData.toelichting && (
                    <>
                        <HorizontalFormGroupRead>
                            <FormLabel>Toelichting</FormLabel>
                            <InputContainer>
                                <FormParagraph>{props.request.customControlData.toelichting}</FormParagraph>
                            </InputContainer>
                        </HorizontalFormGroupRead>
                    </>
                )}
                {showAnamnese() && <FormDivider />}
                <MedicationsRead
                    entity={ProcessEntity.request}
                    entityId={props.request.id}
                    onContentLoaded={async (hasContent) => {
                        setIsFooterVisible(hasContent);
                    }}
                    patientUsesMedication={props.request.patientUsesMedication}
                />
                <ProfessionalSummaryDataRead request={props.request} />
                <TxcsMedicationsList
                    onContentLoaded={async (hasContent) => {
                        if (hasContent) {
                            setIsFooterVisible(true);
                        }
                    }}
                    requestId={props.request.id}
                />
                <ProfileImages request={props.request} />
                <TcorFollowingSteps />
            </CustomControlCollapseBody>

            {isFooterVisible && (
                <ProcessItemFooter
                    isItemOpen={props.itemState.isOpen}
                    labelLess={translateKey('request.hideRequest')}
                    labelMore={translateKey('request.showRequest')}
                    onToggle={async (isOpened) => await props.updateItemState({ ...props.itemState, isOpen: isOpened })}
                />
            )}

            <RequestReadButtons {...props} />
            <HideRequestedExaminationInTaskListAndHomeCheckbox
                isLastItem={props.isLastItem}
                reload={props.reloadRequest}
                request={props.request}
            />
        </ProcessContainer>
    );

    function showAnamnese() {
        const customControlData = props.request.customControlData;

        return Boolean(
            customControlData.toelichting ||
                customControlData.voorgeschiedenis ||
                customControlData.risicofactoren ||
                customControlData.lichamelijkOnderzoek
        );
    }

    function checkIsFooterVisible() {
        const customControlData = props.request.customControlData;
        if (
            showAnamnese() ||
            (customControlData.gewicht !== null && customControlData.lengte !== null) ||
            customControlData.bloeddruk_diastolisch ||
            customControlData.bloeddruk_systolisch ||
            props.request.status === RequestStatus.requested
        ) {
            setIsFooterVisible(true);
        } else {
            setIsFooterVisible(false);
        }
    }
}
